import type { DELIVERY_PROPOSAL_CONTRACT_STATUS } from "@/features/offer-cargo-process-status/badge-status";
import { formatMoney } from "@/shared/lib/currency";
import { toLocaleTextAsDateTime } from "@/shared/lib/datetime";
import { BaseDivider } from "@/shared/ui/base-divider";
import { OfferCargoStatusBadge } from "@/widgets/offer-cargo-status-badge";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, Grid2, Stack, Typography } from "@mui/material";
import { Link } from "@tanstack/react-router";

type Props = {
  id: number;
  companyName: string;
  deliveryProposalContractStatus: DELIVERY_PROPOSAL_CONTRACT_STATUS;
  proposedAt: string;
  responseDeadlineAt: Date;
  requiredShippingFee: number;
  proposedFeeDiff: number | undefined;
};

export const MobileDeliveryProposalListItem: React.FC<Props> = ({
  id,
  companyName,
  deliveryProposalContractStatus,
  proposedAt,
  responseDeadlineAt,
  requiredShippingFee,
  proposedFeeDiff,
}) => {
  return (
    <Box
      key={id}
      sx={{
        backgrounColor: "white",
        boxShadow: "0px 1px 10px 0px #0000001A",
        borderRadius: "18px",
        border: "1px solid #E4E5E5",
        p: 1.5,
      }}
    >
      <Stack direction="column" alignItems="stretch" gap={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Badge label="企業名" />
          <Typography fontSize="14px" color="grey.900">
            {companyName}
          </Typography>
        </Stack>

        <BaseDivider />

        <Grid2
          container
          sx={{
            border: "1px solid #E4E5E5",
            borderRadius: "8px",
            fontSize: "12px",
            color: "grey.700",
            textAlign: "center",
          }}
        >
          {/* ヘッダ */}
          <Grid2
            size={3}
            px={1}
            py={0.75}
            borderRight="1px solid #E4E5E5"
            borderBottom="1px solid #E4E5E5"
          >
            契約状況
          </Grid2>
          <Grid2
            size={3}
            px={1}
            py={0.75}
            borderRight="1px solid #E4E5E5"
            borderBottom="1px solid #E4E5E5"
          >
            返答期限
          </Grid2>
          <Grid2
            size={3}
            px={1}
            py={0.75}
            borderRight="1px solid #E4E5E5"
            borderBottom="1px solid #E4E5E5"
          >
            提案日時
          </Grid2>
          <Grid2 size={3} px={1} py={0.75} borderBottom="1px solid #E4E5E5">
            運賃
          </Grid2>

          {/* 値 */}
          <Grid2 size={3} px={1} py={0.75} borderRight="1px solid #E4E5E5">
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <OfferCargoStatusBadge
                statusType="delivery_proposal_contract"
                status={deliveryProposalContractStatus}
              />
            </Box>
          </Grid2>
          <Grid2 size={3} px={1} py={0.75} borderRight="1px solid #E4E5E5">
            {toLocaleTextAsDateTime(responseDeadlineAt).replace(
              /^\d\d\d\d\//,
              "",
            )}
          </Grid2>
          <Grid2 size={3} px={1} py={0.75} borderRight="1px solid #E4E5E5">
            {proposedAt.replace(/^\d\d\d\d\//, "")}
          </Grid2>
          <Grid2 size={3} px={1} py={0.75}>
            <Stack spacing={0} alignItems="end">
              <Typography variant="body1">
                {formatMoney(requiredShippingFee)}
              </Typography>
              {proposedFeeDiff !== 0 && proposedFeeDiff !== undefined && (
                <Typography variant="caption">
                  {proposedFeeDiff > 0 ? "+" : "-"}
                  {formatMoney(Math.abs(proposedFeeDiff))}
                </Typography>
              )}
            </Stack>
          </Grid2>
        </Grid2>

        <Button
          component={Link}
          to={`/shipper/delivery-proposals/${id}`}
          sx={{
            alignSelf: "stretch",
            position: "relative",
            py: "10px",
            borderRadius: "24px",
            backgroundColor: "grey.100",
            fontSize: "14px",
            fontWeight: 700,
            color: "grey.900",
          }}
        >
          提案詳細を確認
          <ChevronRightIcon
            width={20}
            height={20}
            sx={{ position: "absolute", right: "10px", top: "10px" }}
          />
        </Button>
      </Stack>
    </Box>
  );
};

const Badge = ({ label }: { label: string }) => {
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.5,
        borderRadius: "2px",
        backgroundColor: "grey.100",
      }}
    >
      <Typography fontSize="12px">{label}</Typography>
    </Box>
  );
};
